import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Timer } from 'lucide-react';
import { getPublicSubathon, Subathon } from '../../../api/payment/subathon.api';
import { clockSkewMs, formatHms, remainingAt } from '../../../utils/subathon';
import usePollWhile from '../../../hooks/use-poll-while/use-poll-while';

const RELOAD_MS = 30_000;

/**
 * Thẻ subathon hiển thị cho NGƯỜI XEM trên trang cá nhân creator (tab Donate) — ai vào trang mà
 * không xem overlay livestream vẫn biết creator đang chạy subathon và còn bao lâu.
 *
 * Tải lại mỗi 30 giây trong khi tab đang được xem (không tải khi tab ẩn/thu nhỏ, đỡ tốn lời
 * gọi vô ích) + tải lại ngay khi quay lại tab, để khách đứng nguyên trên trang vẫn thấy subathon
 * mới bắt đầu hoặc vừa kết thúc mà không cần bấm F5.
 */
const CreatorSubathon: React.FC<{ userId?: string }> = ({ userId }) => {
  const [sub, setSub] = useState<Subathon | null>(null);
  const [now, setNow] = useState(() => Date.now());
  const [visible, setVisible] = useState(
    () => typeof document === 'undefined' || document.visibilityState === 'visible'
  );
  const skewRef = useRef(0);

  const load = useCallback(() => {
    if (!userId) return;
    getPublicSubathon(userId)
      .then((res) => {
        skewRef.current = clockSkewMs(res.serverNow);
        setSub(res);
      })
      .catch(() => setSub(null));
  }, [userId]);

  useEffect(() => {
    const onVisibility = () => setVisible(document.visibilityState === 'visible');
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, []);

  // Tải lần đầu, tải lại khi đổi userId, và tải lại ngay khi tab vừa hiện lại.
  useEffect(() => {
    if (visible) load();
  }, [load, visible]);

  usePollWhile(visible, load, RELOAD_MS);

  const isCounting = sub?.status === 'Running' && !sub.isPaused;
  usePollWhile(isCounting, () => setNow(Date.now()), 1000);

  // Chưa từng bắt đầu, hoặc đã kết thúc (quá 10 phút thì BE cũng trả exists=false) -> không hiện gì.
  if (!sub?.exists || sub.status === 'Ended') return null;

  const remaining = remainingAt(sub, now, skewRef.current);
  const clock = formatHms(remaining);
  const label = sub.isPaused ? 'Subathon tạm dừng' : 'Subathon đang diễn ra';

  return (
    <div className="mt-8 box rounded-2xl p-4 md:p-6">
      <div className="flex items-center gap-2">
        <Timer className="text-blue-600" size={20} />
        <h3 className="text-lg font-semibold text-gray-800 dark:text-white">{label}</h3>
      </div>

      <p className="mt-2 text-base font-semibold text-gray-800 dark:text-white">
        Còn {clock} · cứ {Number(sub.caPerMinute).toLocaleString('vi-VN')} Ca thêm 1 phút
      </p>
    </div>
  );
};

export default CreatorSubathon;
